import {
	FormControl,
	MenuItem,
	Grid,
	Typography,
	InputLabel,
} from '@mui/material';
import Select, { SelectProps } from '@mui/material/Select';

import { useEffect, useState } from 'react';

import { ReactComponent as DeleteIcon } from 'common/assets/images/delete-icon.svg';
import { ReactComponent as ExpandMoreRoundedIcon } from 'common/assets/images/select-chevron.svg';
import ButtonComp from 'common/components/Button/ButtonComp';
import SearchSelectDropdownEle, {
	ISelectOption,
} from 'common/components/SearchableSelectDropdown/SearchSelectDropdown';
import { toSentenceCase } from 'common/utils/utilFunctions';

import { IBusinessUnits } from 'features/RegulationInventory/api/type-def';
import { IBusinessAssociation } from 'features/RegulationInventory/RegulationInventoryLandingPage/MappingTable/MappingTable';

import styles from './BusinessUnitDropdown.module.scss';

type IBusinessUnitProps = Omit<SelectProps, 'onChange'> & {
	businessUnitOptions: ISelectOption[];
	assignedUserOptions: IBusinessAssociation[];
	businessUnitAssociations: IBusinessUnits[];
	isMultiSelect?: boolean;
	saveHandler: (val: IBUDropDownOptions[], shouldEnableApply?: boolean) => void;
};

export interface IBUDropDownOptions {
	businessUnit: string;
	assignedUser?: string;
	businessUnitId: string;
	assignedUserId: string;
	assignedUserName: string;
	allAssignedUsers?: ISelectOption[];
}

const BUDropDownIntialValues: IBUDropDownOptions = {
	businessUnit: '',
	assignedUser: '',
	businessUnitId: '',
	assignedUserId: '',
	assignedUserName: '',
	allAssignedUsers: [],
};

const { backgroundColor, borderColor, textFieldColor, deleteIconFillColor } =
	styles;

const BusinessUnitDropdown = ({
	businessUnitOptions,
	businessUnitAssociations,
	assignedUserOptions,
	isMultiSelect = false,
	saveHandler,
	...otherProps
}: IBusinessUnitProps) => {
	const [open, setOpen] = useState<boolean>(false);

	const [businessUnitDropDownSearchText, setBusinessUnitDropDownSearchText] =
		useState<string>('');
	const [assignedUsersDropDownSearchText, setAssignedUsersDropDownSearchText] =
		useState<string>('');

	const getAssignedUsers = (name: string): ISelectOption[] => {
		let users: ISelectOption[] = [];
		assignedUserOptions.forEach((res) => {
			if (res.businessUnit === name) {
				users = res.assignedUsers.map((res) => ({
					name: res.businessUnitUserName,
					id: res.businessUnitUserId,
				}));
			}
		});
		return users;
	};
	const handleMapFunc = (res: any) => {
		const element: IBUDropDownOptions = {
			businessUnit: res.businessUnitName,
			assignedUser: `${res.assignedUserName || ''}`,
			businessUnitId: res.businessUnitId,
			assignedUserId: res.assignedUserId,
			assignedUserName: res.assignedUserName,
			allAssignedUsers: getAssignedUsers(res.businessUnitName),
		};
		return element;
	};
	const [buDropDownOptions, setBuDropDownOptions] = useState<
		IBUDropDownOptions[]
	>(
		businessUnitAssociations.length > 0
			? businessUnitAssociations.map((res) => handleMapFunc(res))
			: [BUDropDownIntialValues],
	);

	const [usedBusinessUnits, setUsedBusinessUnits] =
		useState<ISelectOption[]>(businessUnitOptions);

	const handleOpen = () => {
		setOpen(true);
	};

	const handleClose = () => {
		setOpen((prev) => !prev);
	};

	const filteredOptions = buDropDownOptions.filter(
		(res) =>
			res.businessUnit &&
			res.assignedUser &&
			res.assignedUser?.split(' ').join('').length > 0,
	);

	const shouldEnableApply = buDropDownOptions.every(
		(res) =>
			res.businessUnit &&
			res.assignedUser &&
			res.assignedUser?.split(' ').join('').length > 0,
	);

	const saveButtonHandler = () => {
		saveHandler(filteredOptions, shouldEnableApply);
		handleClose();
	};

	const getBusinessUnitId = (name: string) => {
		let id = '';
		businessUnitOptions.forEach((res) => {
			if (res.name === name) {
				id = res.id;
			}
		});
		return id;
	};

	const getAssignedToUserId = (name: string) => {
		let id = '';
		buDropDownOptions.forEach((res) => {
			res.allAssignedUsers?.forEach((ele) => {
				if (ele.name === name) {
					id = ele.id;
				}
			});
		});
		return id;
	};

	const onBusinessUnitChange = (
		event: React.ChangeEvent<{ value: string }>,
		index: number,
	) => {
		event.preventDefault();
		event.stopPropagation();
		const aval = buDropDownOptions.map((ele) => ele.businessUnit);
		const updatedValues = buDropDownOptions.map((res, i) => {
			if (i === index) {
				const available = businessUnitOptions.filter(
					(ele) =>
						ele.name !== event.target.value &&
						!aval.includes(event.target.value),
				);
				setUsedBusinessUnits(available);
				return {
					...res,
					businessUnit: event.target.value,
					assignedUser: '',
					assignedUserId: '',
					assignedUserFirstName: '',
					assignedUserLastName: '',
					businessUnitId: getBusinessUnitId(event.target.value),
					allAssignedUsers: getAssignedUsers(event.target.value),
				};
			}
			return res;
		});
		setBuDropDownOptions(updatedValues);
	};

	const onAssignedToUserChange = (
		event: React.ChangeEvent<{ value: string }>,
		index: number,
	) => {
		event.preventDefault();
		event.stopPropagation();
		const updatedValues = buDropDownOptions.map((res, i) => {
			if (i === index) {
				return {
					...res,
					assignedUser: event.target.value,
					assignedUserId: getAssignedToUserId(event.target.value),
					assignedUserName: event.target.value,
				};
			}
			return res;
		});
		setBuDropDownOptions(updatedValues);
	};

	const addBusinessUnitClicked = () => {
		if (buDropDownOptions.length <= businessUnitOptions.length - 1) {
			setBuDropDownOptions((prev) => [...prev, BUDropDownIntialValues]);
		}
	};

	const deleteRowClicked = (e: any, value: number) => {
		e.stopPropagation();
		setBuDropDownOptions((prev) => prev.filter((_, i) => i !== value));
	};

	useEffect(() => {
		const units = buDropDownOptions.map((res) => res.businessUnit);
		const values = businessUnitOptions.filter(
			(res) => !units.includes(res.name),
		);
		setUsedBusinessUnits(values);
	}, [buDropDownOptions]);

	const multiDropDownValues = filteredOptions
		.map((res) => res.businessUnit)
		.join(', ');

	const isAddBusinessUnitDisabled =
		buDropDownOptions.length >= businessUnitOptions.length;

	const date = new Date();

	const getFilteredOptions = (options: ISelectOption[], searchWord: string) => {
		if (searchWord) {
			return options.filter(
				(res) =>
					res.name && res.name.toLowerCase().includes(searchWord.toLowerCase()),
			);
		}
		return options;
	};

	return (
		<FormControl
			sx={{ width: '100%' }}
			variant="filled"
			data-testid="form-control"
			size="small"
		>
			{isMultiSelect && (
				<InputLabel
					id="test-select-label"
					shrink={multiDropDownValues.length > 0}
					className={styles['label-style']}
					sx={{
						'&.Mui-focused': {
							color: textFieldColor,
						},
					}}
				>
					{toSentenceCase('Business Unit')}
				</InputLabel>
			)}
			<Select
				open={open}
				onOpen={handleOpen}
				onClick={handleClose}
				labelId="filled-select-adornment"
				IconComponent={ExpandMoreRoundedIcon}
				size="small"
				{...otherProps}
				label={toSentenceCase('Business Unit')}
				renderValue={() => {
					if (!isMultiSelect) {
						return (
							<div>
								{businessUnitAssociations.map((item, index) => {
									if (index === 0) {
										return (
											<Typography
												className={styles['text-section-common']}
												key={item.businessUnitId}
											>
												{item.businessUnitName}
											</Typography>
										);
									}
									return (
										<Typography
											className={styles['text-section-common']}
											key={item.businessUnitId}
										>
											<br />
											{item.businessUnitName}
										</Typography>
									);
								})}
							</div>
						);
					}
					return (
						<div className={styles['text-business-unit']}>
							{multiDropDownValues}
						</div>
					);
				}}
				className={otherProps.className}
				sx={{
					backgroundColor,
					border: '1px solid',
					borderColor,
					width: isMultiSelect ? '14rem' : '11.5rem',
					height: isMultiSelect ? '2.6rem' : 'auto',
				}}
				SelectDisplayProps={{
					style: { padding: '6px 6px 0px 8px' },
				}}
				variant="filled"
				inputProps={{
					'data-testid': 'select-id',
					'aria-label': 'Without label',
					style: { paddingBottom: '0px' },
					...otherProps.inputProps,
				}}
				displayEmpty
				disableUnderline
				MenuProps={{
					anchorOrigin: {
						vertical: isMultiSelect ? 'top' : 'bottom',
						horizontal: isMultiSelect ? 'center' : 'left',
					},
					transformOrigin: {
						vertical: isMultiSelect ? 'bottom' : 'top',
						horizontal: isMultiSelect ? 'center' : 'left',
					},
					sx: {
						'&& .Mui-selected': {
							backgroundColor,
						},
					},
					PaperProps: {
						className: styles['menu-paper'],
					},
				}}
			>
				{buDropDownOptions.map((res, i) => (
					<MenuItem
						onClick={handleClose}
						key={date.getUTCMilliseconds().toString(36).substring(2, 15)}
					>
						<Grid item display="flex" alignItems="center" gap="20px">
							<SearchSelectDropdownEle
								value={res.businessUnit}
								onChange={(event) => onBusinessUnitChange(event, i)}
								onInputChange={(e: any) =>
									setBusinessUnitDropDownSearchText(e.target.value)
								}
								options={getFilteredOptions(
									usedBusinessUnits,
									businessUnitDropDownSearchText,
								)}
								displayEmpty
								placeholder=""
								textValue={businessUnitDropDownSearchText}
								showMenuItemsTop={isMultiSelect}
								inputProps={{
									'data-testid': 'business-unit',
								}}
								label={toSentenceCase('Business Unit')}
								stylesClassName={styles['width-class']}
							/>
							<SearchSelectDropdownEle
								value={res.assignedUser}
								onChange={(event) => onAssignedToUserChange(event, i)}
								onInputChange={(e: any) =>
									setAssignedUsersDropDownSearchText(e.target.value)
								}
								options={getFilteredOptions(
									res.allAssignedUsers || [],
									assignedUsersDropDownSearchText,
								)}
								displayEmpty
								placeholder=""
								textValue={assignedUsersDropDownSearchText}
								showMenuItemsTop={isMultiSelect}
								label={toSentenceCase('Assigned To')}
								inputProps={{
									'data-testid': 'assigned-to',
								}}
								stylesClassName={styles['width-class']}
							/>
							{i > 0 && (
								<DeleteIcon
									data-testid="delete-icon"
									className={styles['edit-delete-icon-actions']}
									onClick={(event: any) => deleteRowClicked(event, i)}
									fill={deleteIconFillColor}
								/>
							)}
						</Grid>
					</MenuItem>
				))}
				<MenuItem onClick={handleClose}>
					<Grid container alignItems="center" justifyContent="space-between">
						<Grid item display="flex" alignItems="center">
							<ButtonComp
								btnType="secondary"
								disabled={isAddBusinessUnitDisabled}
								clickHandler={addBusinessUnitClicked}
								data-testid="add-business-unit"
							>
								{toSentenceCase('Add Business Unit')}
							</ButtonComp>
						</Grid>
						<Grid item display="flex" alignItems="flex-end" gap="10px">
							<ButtonComp
								btnType="secondary"
								clickHandler={handleClose}
								data-testid="business-unit-cancel"
							>
								Cancel
							</ButtonComp>
							<ButtonComp
								btnType="primary"
								disabled={!shouldEnableApply}
								clickHandler={saveButtonHandler}
								data-testid="business-unit-save"
							>
								Save
							</ButtonComp>
						</Grid>
					</Grid>
				</MenuItem>
			</Select>
		</FormControl>
	);
};

export default BusinessUnitDropdown;
